/**
 * The interactive copilot's blocks (contract v1, 2026-09-26): what a message's blocks are, which asks are answered,
 * and a person's answer to an ask, checked as the server checks it and said in words. Pure, so the thread's cards
 * (routes/twin/imports-copilot-cards.tsx) only show what these decide.
 *
 * A brain message carries asks and proposals; a person's message carries an answer, and where they asked from (a
 * context, 2026-09-28: lib/copilot/ask). Blocks arrive from the server, which checked them, but the page never trusts
 * that: a block it cannot show is dropped, never a crash (blocksOf).
 */
import type {
  CopilotAnswerBlock,
  CopilotAnswerValues,
  CopilotAskBlock,
  CopilotAskInput,
  CopilotBlock,
  CopilotChoice,
  CopilotContextBlock,
  CopilotCorrectionBlock,
  CopilotDateRange,
  CopilotInputKind,
  CopilotMessage,
  CopilotPlantAnswerBlock,
  CopilotProposalBlock,
  CopilotProposalResult,
} from "@/lib/api/copilot-imports";
import type { RecommendationEvent } from "@/lib/api/recommendations";
import type { QuestionStatus } from "@/lib/api/twin-knowledge";
import { aboutFrom } from "@/lib/copilot/ask";
import { formatDay, formatDayRange, isPlantDay } from "@/lib/plant-days";

const INPUT_KINDS = new Set<string>(["date", "date_range", "number", "text", "select"]);
/**
 * The proposal kinds a card shows (copilot-proposals plans and applies each): contract v1's four, and the plant's
 * limits since #1063 (a process window and a scrap target), and an Acquisition study's decisions (acquisition_change,
 * P3c). A proposal of any other kind is dropped.
 */
const PROPOSAL_KINDS = new Set<string>([
  "correlation_group",
  "alarm_rule",
  "report",
  "dashboard_widget",
  "process_window",
  "scrap_target",
  "acquisition_change",
]);
const STATUSES = new Set<string>(["proposed", "applied", "failed", "rejected"]);
/** The longest text answer the server takes (contract: text 1–300). */
export const MAX_TEXT_ANSWER = 300;

export function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** A non-blank string, or null. */
function words(v: unknown): string | null {
  return typeof v === "string" && v.trim() !== "" ? v : null;
}

function choicesOf(v: unknown): CopilotChoice[] {
  if (!Array.isArray(v)) return [];
  const seen = new Set<string>();
  const out: CopilotChoice[] = [];
  for (const c of v) {
    if (!isObject(c)) continue;
    const value = words(c.value);
    const label = words(c.label);
    if (value === null || label === null || seen.has(value)) continue;
    seen.add(value);
    out.push({ value, label });
  }
  return out;
}

function inputOf(v: unknown): CopilotAskInput | null {
  if (!isObject(v)) return null;
  const name = words(v.name);
  const label = words(v.label);
  if (name === null || name === "choice" || label === null) return null;
  if (typeof v.kind !== "string" || !INPUT_KINDS.has(v.kind)) return null;
  const input: CopilotAskInput = { name, kind: v.kind as CopilotInputKind, label };
  if (input.kind === "select") {
    const options = choicesOf(v.options);
    if (options.length === 0) return null;
    input.options = options;
  }
  if (input.kind === "number") {
    if (typeof v.min === "number" && Number.isFinite(v.min)) input.min = v.min;
    if (typeof v.max === "number" && Number.isFinite(v.max)) input.max = v.max;
    if (words(v.unit) !== null) input.unit = (v.unit as string).trim();
  }
  return input;
}

function askOf(v: Record<string, unknown>): CopilotAskBlock | null {
  const id = words(v.id);
  const question = words(v.question);
  if (id === null || question === null) return null;
  const choices = choicesOf(v.choices);
  const inputs: CopilotAskInput[] = [];
  const names = new Set<string>();
  for (const raw of Array.isArray(v.inputs) ? v.inputs : []) {
    const input = inputOf(raw);
    if (input && !names.has(input.name)) {
      names.add(input.name);
      inputs.push(input);
    }
  }
  if (choices.length === 0 && inputs.length === 0) return null;
  return { type: "ask", id, question, choices, multi: v.multi === true, inputs };
}

/**
 * What an applied proposal made, as the server sent it: its string id and url, and the words on what applying did and
 * skipped when it has them (summary); or a rejected one's reason (L2); and nothing else.
 */
export function proposalResultOf(v: Record<string, unknown>): CopilotProposalResult {
  const out: CopilotProposalResult = {};
  if (typeof v.id === "string") out.id = v.id;
  if (typeof v.url === "string") out.url = v.url;
  if (typeof v.summary === "string" && v.summary.trim()) out.summary = v.summary.trim();
  if (typeof v.reason === "string" && v.reason.trim()) out.reason = v.reason.trim();
  return out;
}

const QUESTION_STATUSES = new Set<string>(["draft", "approved", "asked", "answered", "closed", "withdrawn"]);

/** A recommendation's latest event as a correction card carries it: the fields its state line reads, or nothing. */
function eventOf(v: unknown): RecommendationEvent | null {
  if (!isObject(v) || typeof v.kind !== "string" || typeof v.created_at !== "string") return null;
  return v as unknown as RecommendationEvent;
}

/** A correction card (L2): its key, which verdict, and the person's reason; the platform's words and state when sent. */
function correctionOf(v: Record<string, unknown>): CopilotCorrectionBlock | null {
  const id = words(v.id);
  const key = words(v.key);
  const reason = words(v.reason);
  if (id === null || key === null || reason === null || (v.kind !== "wrong" && v.kind !== "not_applicable")) return null;
  const out: CopilotCorrectionBlock = { type: "correction", id, key, kind: v.kind, reason };
  if (words(v.text) !== null) out.text = (v.text as string).trim();
  const state = eventOf(v.state);
  if (state) out.state = state;
  return out;
}

/** A Record-as-answer card (L2): the question, the person's words, and the question's topic and status when sent. */
function plantAnswerOf(v: Record<string, unknown>): CopilotPlantAnswerBlock | null {
  const id = words(v.id);
  const questionId = words(v.question_id);
  const text = words(v.text);
  if (id === null || questionId === null || text === null) return null;
  const out: CopilotPlantAnswerBlock = { type: "plant_answer", id, question_id: questionId, text };
  if (words(v.topic) !== null) out.topic = (v.topic as string).trim();
  if (typeof v.status === "string" && QUESTION_STATUSES.has(v.status)) out.status = v.status as QuestionStatus;
  if (typeof v.answered_at === "string") out.answered_at = v.answered_at;
  return out;
}

function proposalOf(v: Record<string, unknown>): CopilotProposalBlock | null {
  const id = words(v.id);
  const title = words(v.title);
  if (id === null || title === null || typeof v.kind !== "string" || !PROPOSAL_KINDS.has(v.kind) || !isObject(v.spec)) {
    return null;
  }
  // The spec is kept as it came: planApply and the card's words read each field defensively.
  const p: Record<string, unknown> = { type: "proposal", id, kind: v.kind, title, spec: v.spec };
  if (typeof v.summary === "string") p.summary = v.summary;
  if (words(v.proposal_id) !== null) p.proposal_id = v.proposal_id;
  if (typeof v.status === "string" && STATUSES.has(v.status)) p.status = v.status;
  if (isObject(v.result)) p.result = proposalResultOf(v.result);
  if (typeof v.error === "string") p.error = v.error;
  if (typeof v.decided_at === "string") p.decided_at = v.decided_at;
  return p as unknown as CopilotProposalBlock;
}

function answerBlockOf(v: Record<string, unknown>): CopilotAnswerBlock | null {
  const messageId = words(v.message_id);
  const askId = words(v.ask_id);
  if (messageId === null || askId === null || !isObject(v.values)) return null;
  return { type: "answer", message_id: messageId, ask_id: askId, values: v.values as CopilotAnswerValues };
}

/**
 * Where a person asked from, checked as the server checks it (lib/copilot/ask aboutFrom). A key a later server adds is
 * set aside rather than dropping the block, so the thread still says what the question was about.
 */
function contextBlockOfRaw(v: Record<string, unknown>): CopilotContextBlock | null {
  const rest: Record<string, unknown> = { ...v };
  delete rest.type;
  const about = aboutFrom(rest, false);
  return about ? { type: "context", ...about } : null;
}

/**
 * A message's blocks the page can show: a brain message's asks and proposals, a person's answers and the context they
 * asked from. A message from a server without blocks has none, and a block that is not well formed is dropped (a
 * choice or an input of an ask alone, where the rest of the ask stands).
 */
export function blocksOf(m: CopilotMessage): CopilotBlock[] {
  const raw: unknown = m.blocks;
  if (!Array.isArray(raw)) return [];
  const out: CopilotBlock[] = [];
  for (const b of raw) {
    if (!isObject(b)) continue;
    let block: CopilotBlock | null = null;
    if (m.role === "brain" && b.type === "ask") block = askOf(b);
    else if (m.role === "brain" && b.type === "proposal") block = proposalOf(b);
    else if (m.role === "brain" && b.type === "correction") block = correctionOf(b);
    else if (m.role === "brain" && b.type === "plant_answer") block = plantAnswerOf(b);
    else if (m.role === "user" && b.type === "answer") block = answerBlockOf(b);
    else if (m.role === "user" && b.type === "context") block = contextBlockOfRaw(b);
    if (block) out.push(block);
  }
  return out;
}

/** The context a person's message was asked from, or null (the thread's "About: <label>" line under it). */
export function contextOfMessage(m: CopilotMessage): CopilotContextBlock | null {
  for (const b of blocksOf(m)) if (b.type === "context") return b;
  return null;
}

/** An ask's key in a thread: the brain message that carries it, and its id. */
export function answerKey(messageId: string, askId: string): string {
  return `${messageId}/${askId}`;
}

/** How an ask was answered: the person's message, its words, and the values. */
export interface AnsweredAsk {
  messageId: string;
  body: string;
  values: CopilotAnswerValues;
}

/**
 * The thread's answered asks, by answerKey: an ask is answered when a person's message carries an answer block naming
 * its message and id. The first answer counts (the server refuses another).
 */
export function answeredAsks(messages: CopilotMessage[]): Map<string, AnsweredAsk> {
  const out = new Map<string, AnsweredAsk>();
  for (const m of messages) {
    if (m.role !== "user") continue;
    for (const b of blocksOf(m)) {
      if (b.type !== "answer") continue;
      const key = answerKey(b.message_id, b.ask_id);
      if (!out.has(key)) out.set(key, { messageId: m.id, body: m.body, values: b.values });
    }
  }
  return out;
}

/** What a person has picked and typed on an ask's card, as typed: a number is its text until it is checked. */
export interface AskDraft {
  choice: string[];
  inputs: Record<string, string | CopilotDateRange>;
}

export const EMPTY_DRAFT: AskDraft = { choice: [], inputs: {} };

/** Picks a choice: one at a time (picking the chosen one again clears it), or several when the ask allows. */
export function withChoice(ask: CopilotAskBlock, draft: AskDraft, value: string): AskDraft {
  const chosen = draft.choice.includes(value);
  if (ask.multi) {
    return { ...draft, choice: chosen ? draft.choice.filter((v) => v !== value) : [...draft.choice, value] };
  }
  return { ...draft, choice: chosen ? [] : [value] };
}

/** What was typed in one field. */
export function withInput(draft: AskDraft, name: string, value: string | CopilotDateRange): AskDraft {
  return { ...draft, inputs: { ...draft.inputs, [name]: value } };
}

export type AnswerCheck = { ok: true; values: CopilotAnswerValues } | { ok: false; problem: string };

/** A field's label as the start of a sentence about it: without a trailing colon. */
export function fieldLabel(input: CopilotAskInput): string {
  return input.label.trim().replace(/[\s:]+$/, "");
}

/** An amount with its unit: "1450 °C", and a percentage without a space ("3.8%"). */
export function amountText(value: number, unit?: string): string {
  if (!unit) return String(value);
  return unit === "%" ? `${value}%` : `${value} ${unit}`;
}

const DECIMAL = /^[-+]?(\d+(\.\d*)?|\.\d+)$/;

type FieldValue = { value?: string | number | CopilotDateRange; problem?: string };

/** One field's value, or nothing when it is blank, or what is wrong with it. */
function fieldValue(input: CopilotAskInput, raw: string | CopilotDateRange | undefined): FieldValue {
  if (input.kind === "date_range") {
    const r = typeof raw === "object" && raw !== null ? raw : { from: "", to: "" };
    const from = r.from.trim();
    const to = r.to.trim();
    if (!from && !to) return {};
    if (!from || !to) return { problem: "pick both dates." };
    if (!isPlantDay(from) || !isPlantDay(to)) return { problem: "pick real dates." };
    if (from > to) return { problem: "the first date is after the second." };
    return { value: { from, to } };
  }
  const s = typeof raw === "string" ? raw.trim() : "";
  if (!s) return {};
  switch (input.kind) {
    case "date":
      return isPlantDay(s) ? { value: s } : { problem: "pick a real date." };
    case "number": {
      const n = DECIMAL.test(s) ? Number(s) : NaN;
      if (!Number.isFinite(n)) return { problem: "enter a number." };
      if (input.min !== undefined && n < input.min) return { problem: `at least ${amountText(input.min, input.unit)}.` };
      if (input.max !== undefined && n > input.max) return { problem: `at most ${amountText(input.max, input.unit)}.` };
      return { value: n };
    }
    case "text":
      if (/\p{Cc}/u.test(s)) return { problem: "use one line of plain text." };
      if ([...s].length > MAX_TEXT_ANSWER) return { problem: `at most ${MAX_TEXT_ANSWER} characters.` };
      return { value: s };
    case "select":
      return input.options?.some((o) => o.value === s) ? { value: s } : { problem: "pick one of the options." };
    default:
      return {};
  }
}

/**
 * A person's answer to an ask, checked as the server checks it (contract: answer): the chosen values (exactly one
 * unless the ask allows several), and each filled field's value of its kind. A blank field is not given; a field
 * filled wrong says what is wrong, even when a choice is picked. At least one value.
 */
export function checkAnswer(ask: CopilotAskBlock, draft: AskDraft): AnswerCheck {
  const values: CopilotAnswerValues = {};
  const offered = ask.choices.map((c) => c.value);
  const chosen = draft.choice.filter((v, i, all) => all.indexOf(v) === i);
  if (chosen.some((v) => !offered.includes(v))) return { ok: false, problem: "Pick from the answers offered." };
  if (chosen.length > 1 && !ask.multi) return { ok: false, problem: "Pick one answer." };
  if (chosen.length > 0) values.choice = offered.filter((v) => chosen.includes(v));
  for (const input of ask.inputs) {
    const got = fieldValue(input, draft.inputs[input.name]);
    if (got.problem) return { ok: false, problem: `${fieldLabel(input)}: ${got.problem}` };
    if (got.value !== undefined) values[input.name] = got.value;
  }
  if (Object.keys(values).length > 0) return { ok: true, values };
  if (ask.choices.length > 0 && ask.inputs.length > 0) return { ok: false, problem: "Pick an answer or fill in a field." };
  return { ok: false, problem: ask.choices.length > 0 ? "Pick an answer." : "Fill in a field." };
}

function valueText(input: CopilotAskInput, v: CopilotAnswerValues[string]): string {
  switch (input.kind) {
    case "date":
      return typeof v === "string" && isPlantDay(v) ? formatDay(v) : String(v ?? "");
    case "date_range": {
      const r = v as CopilotDateRange | undefined;
      return r && isPlantDay(r.from) && isPlantDay(r.to) ? formatDayRange(r.from, r.to) : "";
    }
    case "number":
      return typeof v === "number" ? amountText(v, input.unit) : "";
    case "select":
      return input.options?.find((o) => o.value === v)?.label ?? String(v ?? "");
    default:
      return typeof v === "string" ? v : "";
  }
}

/**
 * An answer in words, the person's message that carries it (the brain reads both): the chosen labels, then each
 * field's value in the ask's order ("1–31 Aug 2026", "1450 °C"). With several parts, each field says its label:
 * "Pour temperature; Threshold: 1450 °C".
 */
export function answerText(ask: CopilotAskBlock, values: CopilotAnswerValues): string {
  const parts: { label: string | null; text: string }[] = [];
  const chosen = values.choice ?? [];
  const labels = ask.choices.filter((c) => chosen.includes(c.value)).map((c) => c.label);
  if (labels.length > 0) parts.push({ label: null, text: labels.join(", ") });
  for (const input of ask.inputs) {
    if (values[input.name] === undefined) continue;
    const text = valueText(input, values[input.name]);
    if (text) parts.push({ label: fieldLabel(input), text });
  }
  if (parts.length === 1) return parts[0].text;
  return parts.map((p) => (p.label ? `${p.label}: ${p.text}` : p.text)).join("; ");
}
